// Applications.js — Track all submitted applications
import React, { useEffect, useState, useCallback } from 'react';
import Navbar from './Navbar';
import { useNavigate } from 'react-router-dom';
import API from '../api';

const STATUS_BADGE = {
  Pending:  'badge-orange',
  Shortlisted: 'badge-blue',
  Selected: 'badge-green',
  Rejected: 'badge-red',
};

const STATUS_ICON = {
  Pending:     '⏳',
  Shortlisted: '🔵',
  Selected:    '✅',
  Rejected:    '❌',
};

export default function Applications() {
  const navigate  = useNavigate();
  const student   = JSON.parse(localStorage.getItem('student')) || {};

  const [apps,    setApps]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  const fetchApps = useCallback(async () => {
    if (!student.id) return;
    setLoading(true);
    try {
      const { data } = await API.get(`/applications/${student.id}`);
      setApps(data);
    } catch (err) {
      setError('Could not load applications. Is the backend running?');
    } finally {
      setLoading(false);
    }
  }, [student.id]);

  useEffect(() => { fetchApps(); }, [fetchApps]);

  // Counts by status
  const counts = apps.reduce((acc, a) => {
    acc[a.status] = (acc[a.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <>
      <Navbar />
      <div className="container" style={{ paddingTop: '2rem', paddingBottom: '3rem' }}>

        {/* Header */}
        <div className="page-header">
          <h2>📄 My Applications</h2>
          <p>Track the status of all your placement applications</p>
        </div>

        {/* Status summary pills */}
        {!loading && apps.length > 0 && (
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
            <div style={{
              background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)', padding: '0.6rem 1.1rem',
              fontSize: '0.85rem', fontWeight: 600
            }}>
              Total: <span style={{ color: 'var(--accent-blue)' }}>{apps.length}</span>
            </div>
            {Object.entries(counts).map(([status, count]) => (
              <div key={status} style={{
                background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)', padding: '0.6rem 1.1rem',
                fontSize: '0.85rem', fontWeight: 600
              }}>
                {STATUS_ICON[status] || '•'} {status}:{' '}
                <span style={{ color: status === 'Selected' ? 'var(--accent-green)' : status === 'Rejected' ? 'var(--accent-red)' : 'var(--accent-orange)' }}>
                  {count}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Error */}
        {error && <div className="alert alert-error">⚠️ {error}</div>}

        {/* Loader */}
        {loading && (
          <div className="loader-overlay">
            <div className="spinner" />
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && apps.length === 0 && (
          <div className="empty-state card">
            <div className="empty-icon">📭</div>
            <h3>No applications yet</h3>
            <p>Browse companies and apply to eligible ones to track them here.</p>
            <button
              className="btn btn-primary"
              style={{ marginTop: '1rem' }}
              onClick={() => navigate('/companies')}
            >
              🏢 View Companies
            </button>
          </div>
        )}

        {/* Applications Table */}
        {!loading && apps.length > 0 && (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Company</th>
                  <th>Department</th>
                  <th>Min CGPA</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {apps.map((app, idx) => (
                  <tr key={app.id}>
                    <td style={{ color: 'var(--text-muted)' }}>{idx + 1}</td>
                    <td>
                      <span style={{ fontWeight: 600 }}>{app.company}</span>
                    </td>
                    <td>
                      <span className="badge badge-blue">{app.department}</span>
                    </td>
                    <td style={{ color: 'var(--accent-blue)', fontWeight: 600 }}>
                      {app.min_cgpa}
                    </td>
                    <td>
                      <span className={`badge ${STATUS_BADGE[app.status] || 'badge-orange'}`}>
                        {STATUS_ICON[app.status] || '•'} {app.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Applications Cards (mobile-friendly alternative) */}
        {!loading && apps.length > 0 && (
          <div style={{ marginTop: '1.5rem' }}>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginBottom: '0.75rem' }}>
              💡 Keep checking back — company statuses are updated by the admin.
            </p>
          </div>
        )}

      </div>
    </>
  );
}
